import Vendor from "./Vendor";
import BaseUnit from "./BaseUnit";
import ProductType from "./ProductType";

export default class OrderActivity {
  /**
   * Sorts an array of orders by their startedAt date, prioritizing orders
   * with open tickets, then by status (active, setup, cancelled), and finally
   * by setupDate or startedAt.
   * @param {Object[]} orderActivities - Array of order objects to sort.
   * @returns {Object[]} Sorted array of orders.
   */
  static sort(orderActivities) {
    return orderActivities.sort((a, b) => {
      // Sort orders with open tickets to the top
      const aHasOpenTickets = a.tickets?.some(
        (t) => t.status !== 3 && t.status !== 4,
      );
      const bHasOpenTickets = b.tickets?.some(
        (t) => t.status !== 3 && t.status !== 4,
      );

      // If either order has open tickets, prioritize it
      if (aHasOpenTickets && !bHasOpenTickets) {
        return -1;
      }
      if (!aHasOpenTickets && bHasOpenTickets) {
        return 1;
      }

      // For orders without open tickets, sort by status
      if (!aHasOpenTickets && !bHasOpenTickets) {
        // Sort cancelled orders (status 4) to the very end
        if (a.status === 4 && b.status !== 4) {
          return 1;
        }
        if (a.status !== 4 && b.status === 4) {
          return -1;
        }

        // Sort setup orders (status 3) just before cancelled orders
        if (a.status === 3 && b.status !== 3 && b.status !== 4) {
          return 1;
        }
        if (a.status !== 3 && a.status !== 4 && b.status === 3) {
          return -1;
        }

        // For setup orders, sort by setupDate
        if (a.status === 3 && b.status === 3) {
          return new Date(b.setupDate) - new Date(a.setupDate);
        }
      }

      // Sort by startedAt date for all other cases
      const aDate = a.activity?.startedAt;
      const bDate = b.activity?.startedAt;
      return new Date(bDate) - new Date(aDate);
    });
  }

  /**
   * Retrieves the order activity id for a completed pickup ticket, if one exists.
   * @param {number} orderActivityId - The parent order activity id.
   * @param {Object} chart - The chart data object.
   * @param {Object} orderStatusesIndexed - Indexed order statuses by ID.
   * @returns {number|undefined} The completed pickup ticket order activity id, if found.
   */
  static getCompletedPickupTicketOrderActivityId(
    orderActivityId,
    chart,
    orderStatusesIndexed,
  ) {
    const serviceOrderReason = Object.values(chart.serviceOrderReason).find(
      (serviceOrderReason) => {
        const ticket = chart.orderActivity[serviceOrderReason.currentOrderId];
        const statusName =
          typeof ticket?.status === "string"
            ? ticket.status
            : orderStatusesIndexed[ticket?.status]?.name;

        return (
          serviceOrderReason.previousOrderId === orderActivityId &&
          ticket?.orderType?.toLowerCase() === "pickup" &&
          statusName?.toLowerCase() === "setup"
        );
      },
    );

    return serviceOrderReason?.currentOrderId;
  }

  /**
   * Retrieves the account object for a given order activity from chart data.
   * @param {Object} orderActivity - The order activity object.
   * @param {Object} chart - The chart data object containing account.
   * @returns {Object|null} The account object, or null if not found.
   */
  static getAccount(orderActivity, chart) {
    const activity = chart.activity[orderActivity.id];
    return chart.account[activity.accountId];
  }

  /**
   * Retrieves the account object for a given order activity from chart data.
   * @param {Object} orderActivity - The order activity object.
   * @param {Object} chart - The chart data object containing account.
   * @returns {Object|null} The account object, or null if not found.
   */
  static getAccount(orderActivity, chart) {
    const activity = chart.activity[orderActivity.id];
    return chart.account[activity.accountId];
  }

  /**
   * Finds the service order reason object for a given order activity from
   * chart data.
   * @param {Object} orderActivity - The order activity object.
   * @param {Object} chart - The chart data object containing
   * serviceOrderReason.
   * @returns {Object|undefined} The matching service order reason object, or
   * undefined if not found.
   */
  static getServiceOrderReason(orderActivity, chart) {
    const matchingReasons = Object.values(
      chart.serviceOrderReason || {},
    ).filter(
      (serviceOrderReason) =>
        serviceOrderReason.currentOrderId === orderActivity.id,
    );

    if (matchingReasons.length === 0) {
      return undefined;
    }

    return matchingReasons.sort((a, b) => (b.id || 0) - (a.id || 0))[0];
  }

  /**
   * Traverses parent order relationships to find the root parent order for a
   * given orderActivityId.
   * @param {string|number} childOrderActivityId - The ID of the order
   * activity to start from.
   * @param {Object} chart - The chart data object containing orderActivity
   * and activity.
   * @returns {Object|null} The parent order object, or null if not found.
   */
  static getParentOrder(childOrderActivityId, chart) {
    let currentOrderActivity = chart.orderActivity[childOrderActivityId];

    while (currentOrderActivity) {
      const activity = chart.activity[currentOrderActivity.id];
      const serviceReason = OrderActivity.getServiceOrderReason(
        currentOrderActivity,
        chart,
      );
      const isLinkedAccessoryOrder =
        currentOrderActivity.orderType === "Accessory After Sale" &&
        serviceReason?.previousOrderId;

      if (activity.type === "Order" && !isLinkedAccessoryOrder) {
        return currentOrderActivity;
      }

      if (!serviceReason?.previousOrderId) {
        return null;
      }

      currentOrderActivity = chart.orderActivity[serviceReason.previousOrderId];
    }

    return null;
  }

  /**
   * Calculates approved points for an array of product objects.
   * @param {Object[]} products - Array of product objects.
   * @returns {number} The approved points total, or 0 if no products.
   */
  static getApprovedPoints(products) {
    if (!products || products.length === 0) {
      return 0;
    }

    let total = 0;
    for (const product of products) {
      if (product?.approved === true) {
        const points = Number(product?.points);
        if (!Number.isNaN(points)) {
          total += points;
        }
      }
    }
    return Number(total.toFixed(2));
  }

  /**
   * Retrieves all service quotes associated with a given order from chart
   * data. Parses the documentData field from JSON if present.
   * @param {Object} order - The order object.
   * @param {Object} chartData - The chart data object containing
   * serviceQuote.
   * @returns {Object[]} Array of service quote objects.
   */
  static getServiceQuotes(order, chartData) {
    const serviceQuotes = Object.values(chartData.serviceQuote)?.filter(
      (serviceQuote) => serviceQuote.orderId === order.id,
    );
    const parsedServiceQuotes = serviceQuotes?.map((quote) => {
      const parsedData = quote.documentData
        ? JSON.parse(quote.documentData)
        : null;
      return {
        ...quote,
        documentData: parsedData,
      };
    });

    return parsedServiceQuotes;
  }

  /**
   * Extracts service part catalog items referenced in the documentData of
   * given quotes.
   * @param {Object[]} quotes - Array of service quote objects.
   * @param {Object} chartData - The chart data object containing
   * getServicePartCatalog.
   * @returns {Object[]} Array of service part catalog item objects.
   */
  static getServicePartCatalog(quotes, chartData) {
    if (!Array.isArray(quotes)) {
      return [];
    }

    const servicePartCatalog = [];

    quotes.forEach((quote) => {
      if (Array.isArray(quote.documentData)) {
        quote.documentData.forEach((data) => {
          if (data.ItemId) {
            const catalogItem = chartData.servicePartCatalog[data.ItemId];
            if (catalogItem) {
              servicePartCatalog.push(catalogItem);
            }
          }
        });
      }
    });

    return servicePartCatalog;
  }

  /**
   * Retrieves all product objects associated with a given order from chart
   * data.
   * @param {Object} orderActivity - The order object.
   * @param {Object} chartData - The chart data object containing
   * orderActivityProducts and product.
   * @returns {Object[]} Array of product objects for the order.
   */
  static getProducts(orderActivity, chartData) {
    return Object.values(chartData.orderActivityProducts)
      .filter(
        (orderActivityProduct) =>
          orderActivityProduct?.orderActivityId === orderActivity?.id,
      )
      .map(
        (orderActivityProduct) =>
          chartData.product[orderActivityProduct.productsId],
      );
  }

  /**
   * Determines whether an order activity is complex by checking each of its
   * products. The order activity is considered complex if any single product
   * belongs to a complex product type.
   * @param {Object} orderActivity - The order activity object.
   * @param {Object} chartData - The chart data object containing
   * orderActivityProducts and product.
   * @param {Object} productTypesIndexed - Product types indexed by their id.
   * @returns {boolean} Whether or not the order activity is complex.
   */
  static isComplex(orderActivity, chartData, productTypesIndexed) {
    const products = OrderActivity.getProducts(orderActivity, chartData);

    for (const product of products) {
      const productType = productTypesIndexed[product?.productTypeId];
      if (ProductType.isComplex(productType)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Determines the primary product from an array of products based on points
   * and product type priority.
   * @param {Object[]} products - Array of product objects.
   * @returns {Object|null} The primary product object, or null if none.
   */
  static getPrimaryProduct(products) {
    if (!products?.length) {
      return null;
    }

    const sortedProducts = [...products].sort(
      (firstProduct, secondProduct) =>
        (secondProduct.points || 0) - (firstProduct.points || 0),
    );

    const highestPointProduct = sortedProducts[0];
    const secondHighestPoints = sortedProducts[1]?.points || 0;

    let selectedProduct = null;

    if (highestPointProduct.points > secondHighestPoints) {
      selectedProduct = highestPointProduct;
    }

    const PRODUCT_TYPE_PRIORITIES = {
      "Complex - Group 2": 33,
      "Complex PWC": 3,
      "Complex Base Only": 15,
      "Standard PWC": 2,
      "Complex MWC": 10,
      "Standard MWC": 1,
      "Complex Ancillary": 30,
    };

    if (!selectedProduct) {
      const productsWithMaxPoints = products.filter(
        (product) => product.points === highestPointProduct.points,
      );

      if (productsWithMaxPoints.length > 1) {
        const priorityMatch = Object.values(PRODUCT_TYPE_PRIORITIES).find(
          (priorityId) =>
            productsWithMaxPoints.some(
              (product) => product.productTypeId === priorityId,
            ),
        );

        if (priorityMatch) {
          selectedProduct = productsWithMaxPoints.find(
            (product) => product.productTypeId === priorityMatch,
          );
        }
      }
    }

    if (!selectedProduct) {
      const firstPriorityMatch = Object.values(PRODUCT_TYPE_PRIORITIES).find(
        (priorityId) =>
          products.some((product) => product.productTypeId === priorityId),
      );

      if (firstPriorityMatch) {
        selectedProduct = products.find(
          (product) => product.productTypeId === firstPriorityMatch,
        );
      }
    }

    if (!selectedProduct) {
      selectedProduct = products[0];
    }

    const upgradedProduct = products.find(
      (product) => product.upgradedFromProductId === selectedProduct.id,
    );

    if (upgradedProduct) {
      return upgradedProduct;
    }

    return selectedProduct;
  }

  /**
   * Returns a display name for the primary product, using base unit or
   * product sub type information.
   * @param {Object} product - The product object.
   * @param {Object} baseUnitsIndexed - Indexed base units by ID.
   * @param {Object} vendorsIndexed - Indexed vendors by ID.
   * @param {Object} productSubTypesIndexed - Indexed product sub types by ID.
   * @returns {string|null} The display name for the product, or null if not
   * found.
   */
  static getProductDisplayName(
    product,
    baseUnitsIndexed,
    vendorsIndexed,
    productSubTypesIndexed,
  ) {
    if (!product) {
      return null;
    }

    if (product.baseUnitId) {
      return this.getPrimaryProductFromBaseUnit(
        product,
        baseUnitsIndexed,
        vendorsIndexed,
      );
    } else if (product.productSubTypeId) {
      return this.getPrimaryProductFromProductSubType(
        product,
        productSubTypesIndexed,
      );
    }

    return null;
  }

  /**
   * Constructs a display name for a product using its base unit and vendor
   * information.
   * @param {Object} product - The product object.
   * @param {Object} baseUnitsIndexed - Indexed base units by ID.
   * @param {Object} vendorsIndexed - Indexed vendors by ID.
   * @returns {string|null} The display name, or null if not found.
   */
  static getPrimaryProductFromBaseUnit(
    product,
    baseUnitsIndexed,
    vendorsIndexed,
  ) {
    if (!baseUnitsIndexed[product.baseUnitId]) {
      return null;
    }
    const baseUnit = baseUnitsIndexed[product.baseUnitId];
    const vendorName =
      Vendor.getDisplayName(vendorsIndexed[baseUnit.vendorId]) || "";
    const baseUnitName =
      BaseUnit.getDisplayName(baseUnitsIndexed[product.baseUnitId]) || "";
    return `${vendorName} ${baseUnitName}`;
  }

  /**
   * Returns the display name for a product using its product sub type.
   * @param {Object} product - The product object.
   * @param {Object} productSubTypesIndexed - Indexed product sub types by ID.
   * @returns {string} The display name, or "Product Not Found" if not found.
   */
  static getPrimaryProductFromProductSubType(product, productSubTypesIndexed) {
    if (!productSubTypesIndexed[product.productSubTypeId]) {
      return "Product Not Found";
    }

    return productSubTypesIndexed[product.productSubTypeId].name;
  }

  /**
   * Generates a Brightree URL for a given order activity using its external
   * ID.
   * @param {Object} orderActivity - The order activity object.
   * @returns {string} The Brightree URL for the order activity.
   */
  static getBrightreeUrl(orderActivity) {
    return `https://brightree.net/F1/02200/OrbitMedical/OrderEntry/frmSOOrderRO.aspx?SalesOrderKey=${orderActivity.brightreeExternalId || order.btSoId}`;
  }
}
